import type { ExperimentSurface } from "@outstanding/shared";
import {
  ensureExperimentAssignments,
  getAssignmentBySurface,
  trackEvent,
  type ExperimentAssignment,
  type TrackEventInput,
} from "@/utils/events";

type SurfaceEvent = Omit<TrackEventInput, "experimentKey">;

interface ExperimentSurfaceOptions {
  /** Event witnessed by this surface; defaults to a bare exposure event. */
  event?: SurfaceEvent;
  /** Preserve surfaces whose exposure cadence is once per component mount. */
  oncePerLoad?: boolean;
  /** Resolve payload only; another call site owns the surface's natural event. */
  trackExposure?: boolean;
  /** Natural events still fire without a tag when no assignment is available. */
  trackWhenUnassigned?: boolean;
}

// Once-per-page-load latch for surfaces that historically dedupe across
// remounts/photo navigation. The assignment key + variant allows a newly
// resolved campaign to emit even if the old one already did.
const exposedAssignmentBySurface = new Map<ExperimentSurface, string>();

/**
 * Resolve a visitor's assignment for a rendered experiment surface and record
 * its exposure with the cadence/event that surface already used.
 */
export async function useExperimentSurface(
  surface: ExperimentSurface,
  options: ExperimentSurfaceOptions = {},
): Promise<ExperimentAssignment | null> {
  const event = options.event ?? { type: "exposure" as const };
  let assignmentsAvailable = true;

  try {
    await ensureExperimentAssignments();
  } catch {
    assignmentsAvailable = false;
  }

  if (!assignmentsAvailable) {
    if (options.trackWhenUnassigned) trackEvent(event);
    return null;
  }

  const assignment = getAssignmentBySurface(surface);
  if (!assignment) {
    if (options.trackWhenUnassigned) trackEvent(event);
    return null;
  }

  if (options.trackExposure === false) return assignment;

  if (options.oncePerLoad) {
    const latchKey = `${assignment.key}:${assignment.variant}`;
    if (exposedAssignmentBySurface.get(surface) === latchKey) return assignment;
    exposedAssignmentBySurface.set(surface, latchKey);
  }

  trackEvent({ ...event, experimentKey: assignment.key });
  return assignment;
}

/** Test hook: clear one surface's module-level exposure latch, or every latch. */
export function resetExperimentSurfaceExposureForTests(
  surface?: ExperimentSurface,
): void {
  if (surface) exposedAssignmentBySurface.delete(surface);
  else exposedAssignmentBySurface.clear();
}
